import { z } from 'zod';
import { ModuleManifest } from '@momentum/shared';

export const DECISION_MAKER_ID = 'decision-maker';

export const decisionMakerManifest = ModuleManifest.parse({
  id: DECISION_MAKER_ID,
  version: '1.0.0',
  name: 'Decision Maker',
  description: 'Four colleagues advise in private, discuss together, and take turns making one shared decision.',
  disposable: false,
  players: { min: 4, max: 4 },
  participation: 'cooperative',
  teams: null,
  voice: { scope: 'shared' },
  capabilities: {
    launchesChildren: false,
    childModuleIds: [],
    usesRandom: false,
    hasPrivateData: true,
    renderer: DECISION_MAKER_ID,
  },
  launchableFromLobby: true,
});

export const DecisionMakerConfig = z
  .object({
    revealMs: z.number().int().min(100).max(10_000).default(1_500),
    countdownMs: z.number().int().min(100).max(10_000).default(3_000),
  })
  .strict();
export type DecisionMakerConfig = z.infer<typeof DecisionMakerConfig>;

export const DecisionMakerAction = z.discriminatedUnion('type', [
  z.object({ type: z.literal('ready') }).strict(),
  z.object({ type: z.literal('suggest'), nodeId: z.string().min(1), optionId: z.string().min(1) }).strict(),
  z.object({ type: z.literal('commit'), nodeId: z.string().min(1), optionId: z.string().min(1) }).strict(),
  z.object({ type: z.literal('continue'), nodeId: z.string().min(1) }).strict(),
  z.object({ type: z.literal('return') }).strict(),
]);
export type DecisionMakerAction = z.infer<typeof DecisionMakerAction>;

export const DecisionMakerPhase = z.enum([
  'reveal',
  'ready',
  'countdown',
  'suggest',
  'discuss',
  'consequence',
  'ending',
  'complete',
]);
export type DecisionMakerPhase = z.infer<typeof DecisionMakerPhase>;

export const DecisionMakerParticipant = z.object({
  id: z.string(),
  displayName: z.string(),
  role: z.string(),
  characterSlot: z.string().nullable(),
});

export const DecisionMakerHistoryEntry = z.object({
  nodeId: z.string(),
  stage: z.number().int().min(1).max(4),
  deciderId: z.string(),
  optionId: z.string(),
  label: z.string(),
  consequence: z.string(),
  clientResponse: z.string(),
  fact: z.string(),
});
export type DecisionMakerHistoryEntry = z.infer<typeof DecisionMakerHistoryEntry>;

export const DecisionMakerEnding = z.object({
  title: z.string(),
  clientLine: z.string(),
  summary: z.string(),
  serviceStatus: z.string(),
  followUp: z.string(),
});
export type DecisionMakerEnding = z.infer<typeof DecisionMakerEnding>;

export const DecisionMakerView = z.object({
  title: z.string(),
  briefing: z.string(),
  objective: z.string(),
  stages: z.array(z.string()),
  phase: DecisionMakerPhase,
  nodeId: z.string(),
  stage: z.number().int().min(1).max(4),
  participants: z.array(DecisionMakerParticipant),
  selfParticipantId: z.string().nullable(),
  deciderId: z.string().nullable(),
  readyIds: z.array(z.string()),
  submittedIds: z.array(z.string()),
  acknowledgedIds: z.array(z.string()),
  returnReadyIds: z.array(z.string()),
  question: z
    .object({ title: z.string(), situation: z.string(), clientLine: z.string() })
    .nullable(),
  options: z.array(z.object({ id: z.string(), label: z.string(), detail: z.string() })),
  mySuggestion: z.string().nullable(),
  suggestions: z.record(z.string(), z.string()),
  history: z.array(DecisionMakerHistoryEntry),
  ending: DecisionMakerEnding.nullable(),
  canReady: z.boolean(),
  canSuggest: z.boolean(),
  canCommit: z.boolean(),
  canContinue: z.boolean(),
  canReturn: z.boolean(),
});
export type DecisionMakerView = z.infer<typeof DecisionMakerView>;

export const DecisionMakerResultPayload = z.object({
  schema: z.literal(1),
  history: z.array(DecisionMakerHistoryEntry),
  ending: DecisionMakerEnding,
});
export type DecisionMakerResultPayload = z.infer<typeof DecisionMakerResultPayload>;
